import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SAMPLES } from '../../data/atomizerSamples';
import { useAtomizer } from './useAtomizer';
const FORCE_CLASS = {
  MUST: 'must',
  NEVER: 'never',
  SHOULD: 'should'
} as const;

/** Counts from 0 to `to` over ~600ms; jumps straight there under reduced motion. */
function useCountUp(to: number) {
  const [n, setN] = useState(to);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 600);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setN(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return n;
}
function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Samples only for now. Upload and paste come back once the platform exposes a
 * public extraction endpoint; runText/runFile stay in useAtomizer for that.
 */
export function Atomizer() {
  const {
    result,
    message,
    libOpen,
    setLibOpen,
    send,
    runSample,
    submitLibrary
  } = useAtomizer();
  const [fields, setFields] = useState({
    name: '',
    email: '',
    company: ''
  });
  const total = result?.total ?? 0;
  const shownTotal = useCountUp(total);
  const shown = result?.rows.length ?? 0;
  return <div id="atomizer-field" style={{
    display: 'flex',
    flexDirection: 'column'
  }} data-id="element-78" data-mp="28hcvx9ya0yqu">
      <div className="picker" data-id="element-79" data-mp="28fa4qmle0zkj">
        <div className="atom-hd" data-id="element-80" data-mp="24eqbvv7uki47">
          <div className="t" data-id="element-81" data-mp="24dyf4h2ikecc">Pick a document.</div>
          <div className="eyebrow" style={{
          letterSpacing: '.1em'
        }} data-id="element-82" data-mp="1zgopp1f4k2oi">
            ATOMIZER
          </div>
        </div>

        <div className="pick-g" data-id="element-83" data-mp="t1e2z5x8d9b6">
          {SAMPLES.map(s => <button key={s.id} type="button" className={`pick${result?.id === s.id ? ' on' : ''}`} aria-pressed={result?.id === s.id} onClick={() => runSample(s.id)} data-id="element-84" data-mp="o1jch3hdalqn">
              <span className="bd" data-id="element-85" data-mp="uh00kqzleoqh">{s.badge}</span>
              <span className="tx" data-id="element-86" data-mp="gi0m2c5snq81">
                <span className="nm" data-id="element-87" data-mp="ggyyj87ldb9f">{s.name}</span>
                <span className="mt" data-id="element-88" data-mp="kjc2br2s2z3k">{s.meta}</span>
              </span>
            </button>)}
        </div>

        <div className="pick-note" data-id="element-89" data-mp="6rfe3y48d9cg">
          Want it on your own document?{' '}
          <Link to="/contact" style={{
          textDecoration: 'underline',
          textUnderlineOffset: 3
        }} data-id="element-90" data-mp="binpv0t196ss">
            Send us one
          </Link>{' '}
          and we’ll run it.
        </div>
        {message && <div className="atom-msg" role="status" data-id="element-91" data-mp="1skxn2tqjwvi">
            {message}
          </div>}
      </div>

      {result && <div className="ledger" aria-live="polite" key={result.id} data-id="element-92" data-mp="3jziizok13cto">
          <div className="ledger-hd" data-id="element-93" data-mp="3oxaknaah6db2">
            <div style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 14,
          flexWrap: 'wrap'
        }} data-id="element-94" data-mp="3ow8zgczqwafm">
              <div className="big" aria-label={`${total} rules found`} data-id="element-95" data-mp="35cjv23kdr0jp">
                {shownTotal}
              </div>
              <div className="lab" data-id="element-96" data-mp="30ei9nbzhep52">RULES FOUND</div>
              {result.binding !== undefined && <div className="lab" style={{
            color: 'var(--amber)'
          }} data-id="element-97" data-mp="3eac974ed825i">
                  {result.binding} binding · {plural(result.conflicts ?? 0, 'conflict', 'conflicts')} found
                </div>}
            </div>
            <div className="src" data-id="element-98" data-mp="3jbt6yr1jz8hv">{result.label}</div>
          </div>
          <div role="table" aria-label="Rules found" data-id="element-99" data-mp="3j9ff0r266cej">
            <div className="lrow h" role="row" data-id="element-100" data-mp="1u09tl04cuye">
              <span role="columnheader" data-id="element-101" data-mp="1wuv1eqyijsv">FORCE</span>
              <span role="columnheader" data-id="element-102" data-mp="3t2e6cgp1wh1u">RULE</span>
              <span role="columnheader" data-id="element-103" data-mp="3t3paihmerqth">TRIGGER</span>
              <span role="columnheader" data-id="element-104" data-mp="awhy04zc3xo1">SOURCE</span>
              <span role="columnheader" data-id="element-105" data-mp="5wn8o5v7jg53">THEIR WORDS</span>
            </div>
            {result.rows.map((r, i) => <div className="lrow in" role="row" key={i} style={{
          animationDelay: `${i * 50}ms`
        }} data-id="element-106" data-mp="20ddiy1fqcfga" data-mpi={i}>
                <span role="cell" data-id="element-107" data-mp="25bewy7yatokw">
                  <span className={`f ${FORCE_CLASS[r.force]}`} data-id="element-108" data-mp="25adpedghrk7x">{r.force}</span>
                </span>
                <span role="cell" data-id="element-109" data-mp="2a36s6vsegihd">
                  {r.rule}
                  {r.note && <span className="cf" data-id="element-110" data-mp="2e3752bzuolmb">{r.note}</span>}
                </span>
                <span role="cell" className="tg" data-id="element-111" data-mp="1hej1ulssn2wy">
                  {r.trigger}
                </span>
                <span role="cell" className="sc" data-id="element-112" data-mp="1mcklp8y1n69h">
                  {r.source}
                </span>
                <span role="cell" className="qw" title={r.words} data-id="element-113" data-mp="1qe64fax9eerl">
                  {r.words}
                </span>
              </div>)}
            <div className="lcount" data-id="element-114" data-mp="1vf24fdehpgrq">
              {shown} of {total} shown
            </div>
          </div>

          <div data-id="element-115" data-mp="31vxjcrv87gzr">
            <div className="ledger-ft" data-id="element-116" data-mp="36sxl4ql1lxl2">
              <div className="s" data-id="element-117" data-mp="36rw6yd8qcuz4">
                Showing {shown} of {total}. The rest have owners, versions, and triggers waiting.
              </div>
              {!libOpen && <button type="button" className="btn btn-s" style={{
            height: 44
          }} onClick={() => setLibOpen(true)} data-id="element-118" data-mp="3bxhv1f69ld6o">
                  Add to your library
                </button>}
            </div>
            {libOpen && <div className="libform" data-id="element-119" data-mp="3357me2b0pfrj">
                <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }} data-id="element-120" data-mp="2y63f5h7nls3u">
                  <div className="t" data-id="element-121" data-mp="2t79wzbhf9cxc">Your library starts here.</div>
                  <div className="b" data-id="element-122" data-mp="2t5ytzdvsw97a">
                    These {total} rules, versioned and owned by you, become the first entries. Tell us where to send the keys.
                  </div>
                  <div className="s" data-id="element-123" data-mp="2k86ea8m62zjd">
                    What you see is the plain-English face of each rule. Underneath, it’s hashed, versioned, traced to its source, and finalized through twelve processes.
                  </div>
                </div>
                {send === 'sent' ? <div className="ok" role="status" data-id="element-124" data-mp="2bx624g3huinw">
                    Got it. We’ll be in touch at {fields.email} with your keys.
                  </div> : <form onSubmit={e => {
            e.preventDefault();
            submitLibrary(fields);
          }} data-id="element-125" data-mp="22y2i99wi5z8c">
                    {([['name', 'Name', 'text'], ['email', 'Work email', 'email'], ['company', 'Company', 'text']] as const).map(([key, label, type]) => <React.Fragment key={key} data-id="element-126" data-mp="2qmc7ymwu774r">
                        <label htmlFor={`lib-${key}`} className="sr" data-id="element-127" data-mp="2ql0x634z4z45">
                          {label}
                        </label>
                        <input id={`lib-${key}`} className="inp" type={type} required placeholder={label} value={fields[key]} onChange={e => setFields(f => ({
                ...f,
                [key]: e.target.value
              }))} data-id="element-128" data-mp="1f7f8sk6xv1jo" />
                      </React.Fragment>)}
                    <button className="btn btn-s" type="submit" disabled={send === 'sending'} style={{
              justifyContent: 'center',
              marginTop: 4
            }} data-id="element-129" data-mp="1bqkj1d9xe6my">
                      {send === 'sending' ? 'Sending…' : 'Start my library'}
                    </button>
                    {send === 'error' && <div className="err" role="alert" data-id="element-130" data-mp="xrb1a962n4gv">
                        That didn’t go through. Try again, or email hello@meetsynthia.ai.
                      </div>}
                  </form>}
              </div>}
          </div>
        </div>}
    </div>;
}